import { useLocation } from "wouter-preact"
import { api_fetch } from "@/api"
import { User } from "@/api"
import { useFormState } from "@/components/form/form_state.tsx"
import { CenterColumnContainer } from "../components/LoginScreen.tsx"
import { setUser } from "./user.tsx"

import { Button } from "@/components/ui/button.tsx"
import { Logo } from "@/app/logo.tsx"
import { Form } from "@/components/form/Form.tsx"
import { EmailInput, PasswordInput } from "@/components/form/Input.tsx"
import { useSearchParams } from "@/utils"

interface RegisterResponse extends User {
  skip_setup: boolean
}

export function Signup() {
  const params = useSearchParams<{ next?: string; token?: string; email?: string; invite?: string }>()

  const [, setLocation] = useLocation()
  const form = useFormState({
    email: params.email || "",
    password: "",
  })

  const hasInvite = !!params.invite

  async function onSubmit() {
    if (form.password.value) {
      // If there's an invitation token, use the invite endpoint
      if (params.invite) {
        const res = await api_fetch<RegisterResponse>("/auth/register_with_invitation", {
          body: {
            invitation_id: params.invite,
            password: form.password.value,
          },
        })
        if (!res.ok) {
          form.set_form_errors([res.error.message])
          return
        }
        setUser(res.value.data)
        const next = params.next ?? "/deductions"
        setLocation(next)
      } else {
        const res = await api_fetch<RegisterResponse>("/auth/register_password", {
          body: form.form_data,
        })
        if (!res.ok) {
          form.set_form_errors([res.error.message])
          return
        }
        setUser(res.value.data)

        const next = params.next ?? (res.value.data.skip_setup ? "/deductions" : "/setup/connect")
        setLocation(next)
      }
    } else {
      const res = await api_fetch("/auth/register_magic_link", {
        body: form.form_data,
      })
      if (!res.ok) {
        form.set_form_errors([res.error.message])
        return
      }
      form.set_form_success("Check your email for a sign-up link!")
    }
  }

  let button_text = hasInvite ? "Create Account" : form.password.value ? "Sign up" : "Email me a sign up link"

  return (
    <CenterColumnContainer title={hasInvite ? "Accept your invitation" : "Sign up for your account"} logo={<Logo />}>
      {hasInvite ? (
        <p className="text-sm font-medium text-gray-900">
          You've been invited to join MarginWiz. Create your password to get started.
        </p>
      ) : (
        <p className="text-sm font-medium text-gray-900">
          After this, we'll walk you through linking your KeHE and email accounts. It should only take a few minutes.
        </p>
      )}
      <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
        <EmailInput label="Email address" required={true} bind={form.email} disabled={hasInvite} />
        <PasswordInput label="Password" required={hasInvite} bind={form.password} />
        <Button
          type="submit"
          variant="tertiary"
          className="w-full btn btn-primary rounded-md focus:outline-hidden focus:ring-1 focus:ring-offset-2">
          {button_text}
        </Button>
      </Form>
    </CenterColumnContainer>
  )
}
